import type { EntryValueViewT } from "@patient-watch/types";

/** Chart series colours — aligned with {@link packages/charts GenericChart}. */
export const HEADLINE_SERIES_COLORS = [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
] as const;

/** Primary total: is_primary_score with no chart_order (WOMAC Total, AOFAS Total, etc.). */
export function isPrimaryHeadlineScore(ev: EntryValueViewT): boolean {
    return ev.is_primary_score === true && ev.chart_order == null;
}

/** Plotted subscale with chart_order metadata. */
export function isSeriesHeadlineScore(ev: EntryValueViewT): boolean {
    return ev.chart_order != null;
}

export interface HeadlineScoresPartition {
    /** Highlighted total (first in display). */
    primary: EntryValueViewT | null;
    /** Subscales ordered by chart_order. */
    series: EntryValueViewT[];
    /** Legacy scores without chart metadata. */
    other: EntryValueViewT[];
}

export function partitionHeadlineScoresForDisplay(entryValues: EntryValueViewT[]): HeadlineScoresPartition {
    const hasChartOrderMeta = entryValues.some((ev) => ev.chart_order != null);

    if (!hasChartOrderMeta) {
        return { primary: null, series: [], other: entryValues };
    }

    const primary = entryValues.find(isPrimaryHeadlineScore) ?? null;
    const series = entryValues
        .filter(isSeriesHeadlineScore)
        .sort((a, b) => (a.chart_order ?? 0) - (b.chart_order ?? 0));
    const primaryId = primary?.entry_value_id;
    const seriesIds = new Set(series.map((ev) => ev.entry_value_id));
    const other = entryValues.filter((ev) => ev.entry_value_id !== primaryId && !seriesIds.has(ev.entry_value_id));

    return { primary, series, other };
}

/**
 * Strips a shared word prefix from score labels, e.g. "WOMAC Pain Score" → "Pain Score".
 * Matches chart tooltip / GenericChart displayLabel behaviour.
 */
export function buildHeadlineDisplayLabel(labels: string[]): (label: string) => string {
    if (labels.length <= 1) return (label) => label;

    const firstLabel = labels[0];
    if (!firstLabel) return (label) => label;

    const parts = firstLabel.split(" ");
    let commonCount = 0;
    for (let i = 1; i <= parts.length; i++) {
        const candidate = `${parts.slice(0, i).join(" ")} `;
        if (labels.every((label) => label.startsWith(candidate))) {
            commonCount = i;
        } else {
            break;
        }
    }

    if (commonCount === 0) return (label) => label;
    const prefix = `${parts.slice(0, commonCount).join(" ")} `;
    return (label: string) => (label.startsWith(prefix) ? label.slice(prefix.length) : label);
}

export function headlineScoreNumericValue(ev: EntryValueViewT): number | null {
    if (ev.number_value !== null && ev.number_value !== undefined && !Number.isNaN(Number(ev.number_value))) {
        return Number(ev.number_value);
    }
    return null;
}
